import { z } from "zod";
const id = z.string().min(1).max(240), digest = z.string().regex(/^[a-f0-9]{64}$/);
const relation = z.union([
    z.object({ kind: z.enum(["CHAPTER", "RECIPE", "BEAT"]), id }).strict(),
    z.object({ kind: z.literal("COMIC_PAGE"), page: z.number().int().positive() }).strict(),
    z
        .object({
        kind: z.literal("COMIC_PANEL"),
        page: z.number().int().positive(),
        panel: z.number().int().positive(),
    })
        .strict(),
]);
export const customerEngineFamilyV1Schema = z.enum([
    "BEDTIME",
    "MY_STORY",
    "OUR_STORY",
    "COOKBOOK",
    "COMIC",
]);
export const customerEngineProofRecoveryCommandV1Schema = z
    .object({
    family: customerEngineFamilyV1Schema,
    jobId: id,
    expectedUpdatedAt: z.string().datetime({ offset: true }),
})
    .strict();
export const customerEngineCurrencyV1Schema = z.enum([
    "CURRENT",
    "STALE",
    "MISSING",
    "UNKNOWN",
    "UNSUPPORTED",
]);
export const customerEngineFileTicketV1Schema = z
    .object({
    bookId: id,
    proofId: id,
    assetId: id,
    checksumSha256: digest,
    downloadUrl: z
        .string()
        .url()
        .refine((value) => new URL(value).protocol === "https:"),
    expiresIn: z.number().int().min(1).max(300),
})
    .strict();
export const customerEngineEditionV1Schema = z
    .object({
    contract: z.literal("LC_CUSTOMER_ENGINE_EDITION_V1"),
    bookId: id,
    family: customerEngineFamilyV1Schema,
    view: z.enum(["WORKING", "ACCEPTED", "ORDERED"]),
    availability: z.enum([
        "CAPTURED",
        "CAPTURED_READER_PENDING",
        "LEGACY_READER_ONLY",
        "HISTORICAL_EVIDENCE_UNAVAILABLE",
        "INCOMPATIBLE",
    ]),
    provenance: z.enum(["CUSTOMER_PERSONAL", "FIXTURE", "UNKNOWN"]),
    immutable: z.boolean(),
    editionId: id.nullable(),
    physicalOrderId: id.nullable(),
    proofId: id.nullable(),
    /** A retained raster from this exact proof, never the cover PDF or another edition. */
    coverPreview: z
        .object({ assetId: id, checksumSha256: digest, ticketPath: z.string().max(2048) })
        .strict()
        .nullable()
        .optional(),
    readerAspect: z.number().positive().max(3).nullable().optional(),
    source: z
        .object({
        artifactId: id,
        revision: z.number().int().nonnegative(),
        digestSha256: digest,
        documentDigestSha256: digest.nullable(),
    })
        .strict()
        .nullable(),
    plan: z
        .object({ planId: id, revision: z.number().int().nonnegative(), digestSha256: digest })
        .strict()
        .nullable(),
    geometry: z
        .object({
        interior: z
            .object({
            widthPoints: z.number().positive(),
            heightPoints: z.number().positive(),
            pageCount: z.number().int().positive(),
        })
            .strict(),
        cover: z
            .object({
            widthPoints: z.number().positive(),
            heightPoints: z.number().positive(),
            pageCount: z.number().int().positive(),
        })
            .strict(),
    })
        .strict()
        .nullable(),
    currentness: z
        .object({
        source: customerEngineCurrencyV1Schema,
        plan: customerEngineCurrencyV1Schema,
        media: customerEngineCurrencyV1Schema,
        proof: customerEngineCurrencyV1Schema,
        cover: customerEngineCurrencyV1Schema,
    })
        .strict(),
    pages: z
        .array(z
        .object({
        pageId: id,
        pageNumber: z.number().int().positive(),
        side: z.enum(["RECTO", "VERSO"]),
        role: z.string().max(120),
        sourceRelations: z.array(relation).max(100),
        ticketPath: z.string().max(2048),
    })
        .strict())
        .max(1000),
    files: z
        .array(z
        .object({ assetId: id, checksumSha256: digest, ticketPath: z.string().max(2048) })
        .strict())
        .max(1003),
    commands: z
        .array(z
        .object({
        intent: z.string().max(80),
        method: z.enum(["GET", "POST", "PATCH", "PUT"]),
        path: z.string().max(2048),
        schemaExport: z.string().max(240),
        state: z.enum([
            "EXISTING_AUTHENTICATED_COMMAND",
            "REQUIRES_EXACT_RECORD_SELECTION",
            "UNSUPPORTED",
        ]),
        providerDispatch: z.enum(["NONE", "EXISTING_SERVER_GATES_REQUIRED"]),
    })
        .strict())
        .max(30),
    issues: z.array(z.string().regex(/^[A-Z0-9_]{1,120}$/)).max(30),
    legacyReaderPath: z.string().max(2048),
    truthBoundary: z.literal("READ_MODEL_NOT_APPROVAL_ENTITLEMENT_OR_PERMISSION_TO_PRINT"),
})
    .strict();
const retainedHttpsUrl = z
    .string()
    .url()
    .refine((value) => new URL(value).protocol === "https:");
export const customerEngineReaderV1Schema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("UNAVAILABLE"), edition: customerEngineEditionV1Schema }).strict(),
    z
        .object({
        state: z.literal("READY"),
        edition: customerEngineEditionV1Schema,
        coverUrl: retainedHttpsUrl.nullable(),
        expiresIn: z.number().int().min(1).max(300),
        pages: z
            .array(z
            .object({ pageId: id, pageNumber: z.number().int().positive(), url: retainedHttpsUrl })
            .strict())
            .min(1)
            .max(1000),
    })
        .strict(),
]);
//# sourceMappingURL=book-engine-editor.js.map